import React from 'react';
import { useTranslation } from 'react-i18next';
import CoachSummaryCard from '../WorkoutDebrief/CoachSummaryCard';
import Markdown from '../common/Markdown';
import WorkoutEnrichmentBlocks from './WorkoutEnrichmentBlocks';
import { HiCheck, HiXMark, HiPencilSquare, HiArrowsRightLeft, HiPlus, HiArrowRight, HiFlag, HiSparkles } from 'react-icons/hi2';

export default function SelectedWorkoutPanel({
  workout,
  canEdit,
  colorClass,
  isToday,
  isCompleted,
  isMissed,
  isFuture,
  canViewDetails,
  executedDebrief,
  executedLoading,
  executedError,
  actualStats,
  onClear,
  onViewDetails,
  onEdit,
  onMove,
  onComplete,
  onMissed,
  onAskCoachForMissed,
  onCreateUnplanned,
  onAddAnotherWorkout,
  schemaVersion = 1,
  eventTitle = null
}) {
  const { t } = useTranslation();

  if (!workout) {
    return (
      <div className="sticky top-4 h-full">
        <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm h-full flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-base font-black text-gray-900 mb-1">
            {t('plan.selected_workout_panel.empty_title')}
          </div>
          <div className="text-sm text-gray-500 max-w-xs mx-auto">
            {t('plan.selected_workout_panel.empty_hint')}
          </div>
        </div>
      </div>
    );
  }

  const typeLabel = String(workout.type || '').replace(/_/g, ' ') || 'workout';
  const focus = workout?.workout_focus ? String(workout.workout_focus).replace(/_/g, ' ') : '';
  const typeLabelWithFocus = focus ? `${typeLabel} — ${focus}` : typeLabel;
  const title = workout.title || '(Untitled)';
  const date = workout.date || null;
  const desc = workout.description || '';
  const plannedKm = Number(workout.distance_km || 0);
  const plannedMin = Number(workout.duration_min || 0);
  const plannedElev = Number(workout.altitude_gain_m || 0);
  const isRest = String(workout.type || '').toLowerCase() === 'rest';

  const actualKm = actualStats?.km ?? null;
  const actualMin = actualStats?.min ?? null;

  const disableMarking = Boolean(isFuture || isCompleted || isMissed);
  const hideEditAndMarking = Boolean(isCompleted);
  const disableMove = Boolean(isRest || isCompleted || isMissed);

  const executedFeedback = (executedDebrief?.user_feedback || '').trim();
  const executedCoachAnalysis = executedDebrief?.coach_analysis || null;

  // Special: "Today" placeholder (no planned session)
  if (workout?.is_unplanned_slot) {
    return (
      <div className="sticky top-4 h-full">
        <div className="bg-white border border-gray-200 rounded-3xl shadow-sm overflow-hidden h-full flex flex-col">
          <div className="p-6 border-b border-gray-100 flex items-start justify-between gap-3 bg-white/50 backdrop-blur-sm">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wide text-indigo-600 bg-indigo-50 border border-indigo-100">
                  {t('plan.today')}
                </span>
              </div>
              <div className="font-black text-xl text-gray-900 truncate leading-tight">
                {t('plan.selected_workout_panel.unplanned_title')}
              </div>
              {workout?.date ? (
                <div className="text-xs text-gray-400 mt-1 font-bold flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                  {workout.date}
                </div>
              ) : null}
            </div>

            <button
              type="button"
              onClick={onClear}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label={t('plan.selected_workout_panel.clear_selection')}
            >
              <HiXMark className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 flex-1 flex flex-col items-center justify-center text-center">
            <div className="w-12 h-12 bg-indigo-50 rounded-full flex items-center justify-center mb-3 text-indigo-400">
               <HiPlus className="w-6 h-6" />
            </div>
            <div className="text-sm text-gray-600 font-medium">
              {t('plan.selected_workout_panel.unplanned_hint')}
            </div>
          </div>

          <div className="p-6 border-t border-gray-100 bg-gray-50/50 mt-auto">
            {canEdit ? (
              <button
                type="button"
                onClick={onCreateUnplanned}
                className="w-full py-3.5 px-4 rounded-full bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-700 shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:-translate-y-0.5 transition-all duration-200 uppercase tracking-wide flex items-center justify-center gap-2"
              >
                <HiPlus className="w-4 h-4" /> {t('plan.selected_workout_panel.unplanned_cta')}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sticky top-4 h-full">
      <div className="bg-white border border-gray-200 rounded-3xl shadow-sm overflow-hidden h-full flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-start justify-between gap-3 bg-white/50 backdrop-blur-sm">
          <div className="min-w-0">
            <div className="flex items-center flex-wrap gap-2 mb-2">
              <span className={`w-2.5 h-2.5 rounded-full ${colorClass || 'bg-gray-400'}`}></span>
              <span className="text-[10px] font-black uppercase tracking-wider text-gray-400">
                {typeLabelWithFocus}
              </span>
              {isToday && (
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-indigo-50 text-indigo-600 border border-indigo-100 uppercase">
                  {t('plan.today')}
                </span>
              )}
              {isCompleted && (
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-green-50 text-green-600 border border-green-100 uppercase flex items-center gap-1">
                  <HiCheck className="w-3 h-3" /> {t('plan.completed')}
                </span>
              )}
              {isMissed && (
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-red-50 text-red-600 border border-red-100 uppercase flex items-center gap-1">
                  <HiXMark className="w-3 h-3" /> {t('plan.missed')}
                </span>
              )}
              {workout?.event_id ? (
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-indigo-600 text-white uppercase flex items-center gap-1 max-w-[180px]">
                  <HiFlag className="w-3 h-3 shrink-0" /> <span className="truncate">{eventTitle || t('events.event_day')}</span>
                </span>
              ) : workout?.prepared_for_event_id && eventTitle ? (
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-indigo-50 text-indigo-700 border border-indigo-100 uppercase flex items-center gap-1 max-w-[200px]">
                  <HiFlag className="w-3 h-3 shrink-0" /> <span className="truncate">{t('events.prepared_for', { title: eventTitle })}</span>
                </span>
              ) : null}
              {workout?.added_by === 'coach' ? (
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-white text-indigo-600 border border-indigo-200 uppercase flex items-center gap-1">
                  <HiSparkles className="w-3 h-3" /> {t('events.added_by_coach')}
                </span>
              ) : null}
            </div>

            <div className="font-black text-xl md:text-2xl text-gray-900 leading-tight mb-1">{title}</div>
            {date ? <div className="text-xs font-bold text-gray-400 mt-1 font-mono flex items-center gap-1.5">
              <span>{workout.day}</span>
              <span className="w-1 h-1 rounded-full bg-gray-300"></span>
              <span>{date}</span>
            </div> : null}
          </div>

          <button
            type="button"
            onClick={onClear}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label={t('plan.selected_workout_panel.clear_selection')}
          >
            <HiXMark className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 flex-1 overflow-y-auto custom-scrollbar">
          
          {/* Metrics */}
          <div className="grid grid-cols-3 gap-3">
             <div className="p-3 rounded-2xl border border-gray-100 bg-gray-50 text-center">
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">
                {t('plan.selected_workout_panel.planned')}
              </div>
              <div className="text-sm font-black text-gray-900 tabular-nums">{plannedKm.toFixed(0)} <span className="text-xs font-bold text-gray-400">km</span></div>
              <div className="text-[10px] font-bold text-gray-500 mt-0.5">{plannedMin.toFixed(0)} min</div>
            </div>
            
            <div className="p-3 rounded-2xl border border-gray-100 bg-gray-50 text-center">
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">
                {t('plan.selected_workout_panel.planned_elev')}
              </div>
              <div className="text-sm font-black text-gray-900 tabular-nums">+{plannedElev.toFixed(0)} <span className="text-xs font-bold text-gray-400">m</span></div>
              <div className="text-[10px] text-gray-300 mt-0.5">—</div>
            </div>

            <div className={`p-3 rounded-2xl border text-center ${actualKm != null ? 'bg-white border-gray-200 shadow-sm' : 'bg-gray-50 border-gray-100 opacity-60'}`}>
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">
                {t('plan.selected_workout_panel.actual')}
              </div>
              <div className="text-sm font-black text-gray-900 tabular-nums">
                {actualKm != null ? `${Number(actualKm).toFixed(1)}` : '—'} <span className="text-xs font-bold text-gray-400">{actualKm != null ? 'km' : ''}</span>
              </div>
              <div className="text-[10px] font-bold text-gray-500 mt-0.5">
                {actualMin != null ? `${Number(actualMin).toFixed(0)} min` : '—'}
              </div>
            </div>
          </div>

          {desc ? (
            <div className="bg-gray-50/50 rounded-2xl p-4 border border-gray-100">
               <Markdown className="text-sm text-gray-700 leading-relaxed [&_p]:my-2 [&_strong]:text-gray-900 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1">
                {desc}
              </Markdown>
            </div>
          ) : (
            <div className="text-sm text-gray-400 italic text-center py-4">{t('plan.selected_workout_panel.no_description')}</div>
          )}

          <WorkoutEnrichmentBlocks workout={workout} />

          {/* Skeleton Loader */}
          {!workout.why && !workout.fueling && !workout.technique && (schemaVersion >= 2 && !workout.is_unplanned_slot) && (
            <div className="space-y-3 animate-pulse mt-2">
              <div className="h-4 bg-gray-100 rounded w-3/4"></div>
              <div className="h-4 bg-gray-100 rounded w-1/2"></div>
              <div className="h-4 bg-gray-100 rounded w-5/6"></div>
            </div>
          )}

          {canViewDetails ? (
            <div className="border-t border-gray-100 pt-6">
              {executedLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <div className="text-xs font-bold text-gray-400 uppercase">{t('plan.selected_workout_panel.executed_loading')}</div>
                </div>
              ) : executedError ? (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 text-sm font-medium text-center border border-red-100">
                  {t('plan.selected_workout_panel.executed_error')}
                </div>
              ) : (
                <>
                  <div className="mt-0">
                    <div className="text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">
                      {t('workout.feedback_label')}
                    </div>
                    <div className="text-sm text-gray-700 whitespace-pre-line bg-gray-50/50 rounded-2xl p-4 border border-gray-100 italic">
                      "{executedFeedback || '—'}"
                    </div>
                  </div>

                  <div className="mt-6">
                    <CoachSummaryCard coachAnalysis={executedCoachAnalysis} />
                  </div>

                  {executedCoachAnalysis?.version === 2 && executedCoachAnalysis?.coach_read ? (
                    <div className="mt-4 rounded-2xl border border-indigo-100 bg-indigo-50/50 p-4">
                      <div className="text-[10px] font-black uppercase tracking-wider text-indigo-700 mb-1">
                        {t('coach_options.coach_read_title')}
                      </div>
                      <div className="text-sm text-gray-800 leading-relaxed line-clamp-4">{executedCoachAnalysis.coach_read}</div>
                      {Array.isArray(executedCoachAnalysis.options) && executedCoachAnalysis.options.length > 0 && !executedDebrief?.option_decision ? (
                        <div className="mt-2 text-xs font-black text-indigo-700">
                          {t('coach_options.pending_hint', { count: executedCoachAnalysis.options.length })}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer actions */}
        <div className="p-4 md:p-6 border-t border-gray-100 bg-gray-50/50 mt-auto sticky bottom-0">
          <div className="flex flex-col gap-3">
             {canViewDetails ? (
              <button
                type="button"
                onClick={onViewDetails}
                className="w-full py-3.5 px-4 rounded-full bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-700 shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:-translate-y-0.5 transition-all duration-200 uppercase tracking-wide flex items-center justify-center gap-2"
              >
                {t('workout.view_details')} <HiArrowRight className="w-4 h-4" />
              </button>
            ) : null}

            {canEdit && !hideEditAndMarking ? (
              <>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                   <button
                    type="button"
                    onClick={onComplete}
                    disabled={disableMarking}
                    className={[
                      'w-full py-3 px-4 rounded-full font-bold text-sm transition-all duration-200 uppercase tracking-wide flex items-center justify-center gap-2',
                      disableMarking
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700 shadow-lg shadow-green-500/30 hover:shadow-green-500/40 hover:-translate-y-0.5'
                    ].join(' ')}
                    title={disableMarking && isFuture ? t('plan.selected_workout_panel.disabled_future') : undefined}
                  >
                    <HiCheck className="w-4 h-4" /> {t('plan.mark_complete')}
                  </button>

                  {typeof onAddAnotherWorkout === 'function' ? (
                     <button
                      type="button"
                      onClick={onAddAnotherWorkout}
                      className="w-full py-3 px-4 rounded-full font-bold text-sm transition-all duration-200 uppercase tracking-wide flex items-center justify-center gap-2 bg-white border-2 border-indigo-100 text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50"
                    >
                      <HiPlus className="w-4 h-4" /> {t('plan.selected_workout_panel.add_another')}
                    </button>
                  ) : null}
                 </div>

                 <div className="grid grid-cols-2 gap-2 mt-1">
                    <button
                      type="button"
                      onClick={onEdit}
                      className="py-2.5 px-2 rounded-xl font-bold bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors text-xs flex items-center justify-center gap-1.5"
                    >
                      <HiPencilSquare className="w-4 h-4" /> {t('plan.selected_workout_panel.edit')}
                    </button>

                    {onMove && !isRest ? (
                      <button
                        type="button"
                        onClick={onMove}
                        disabled={disableMove}
                        className={[
                          'py-2.5 px-2 rounded-xl font-bold transition-colors text-xs flex items-center justify-center gap-1.5',
                          disableMove
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        ].join(' ')}
                        title={disableMove ? t('plan.move.disabled') : undefined}
                      >
                        <HiArrowsRightLeft className="w-4 h-4" /> {t('plan.move.button')}
                      </button>
                    ) : null}

                    <button
                      type="button"
                      onClick={onMissed}
                      disabled={disableMarking}
                      className={[
                        'py-2.5 px-2 rounded-xl font-bold transition-colors text-xs flex items-center justify-center gap-1.5',
                        disableMarking
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white border border-red-100 text-red-600 hover:bg-red-50'
                      ].join(' ')}
                      title={disableMarking && isFuture ? t('plan.selected_workout_panel.disabled_future') : undefined}
                    >
                      <HiXMark className="w-4 h-4" /> {t('plan.missed_no_change')}
                    </button>
                    {onAskCoachForMissed ? (
                      <button
                        type="button"
                        onClick={onAskCoachForMissed}
                        disabled={disableMarking}
                        className={[
                          'py-2.5 px-2 rounded-xl font-bold transition-colors text-xs flex items-center justify-center gap-1.5',
                          disableMarking
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white border border-indigo-100 text-indigo-600 hover:bg-indigo-50'
                        ].join(' ')}
                        title={disableMarking && isFuture ? t('plan.selected_workout_panel.disabled_future') : undefined}
                      >
                        <HiArrowRight className="w-4 h-4" /> {t('plan.ask_coach_short')}
                      </button>
                    ) : null}
                 </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}


